import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api, ConflictError } from '../api/client';
import type { InventoryLocation, LocationGroup } from '../api/types';
import { useApp } from '../state/AppContext';
import { ConflictBanner, EmptyRow, ErrorBanner, SortableHeader, useTableSort } from '../components/ui';

interface LocationDraft {
  locationId: number;
  code: string;
  regionCode: string;
  groupId: number;
  locationTypeId: number;
  city: string;
  isActive: boolean;
  rowVersionNo: number;
}

const toDraft = (l: InventoryLocation): LocationDraft => ({
  locationId: l.locationId, code: l.code, regionCode: l.regionCode, groupId: l.groupId,
  locationTypeId: l.locationTypeId, city: l.city ?? '', isActive: l.isActive,
  rowVersionNo: l.rowVersionNo,
});

/**
 * Every truck, supply unit and facility that is counted. Renaming a location
 * keeps its history, because counts point at the location rather than its
 * name; a location that stops being counted is made inactive, never deleted.
 */
export function Locations() {
  const { reference, regionName, groupName, typeOf, canEditSetup } = useApp();
  const queryClient = useQueryClient();

  // Arriving from Supplies: only the locations whose count list has that supply.
  const [params, setParams] = useSearchParams();
  const supplyId = Number(params.get('supply')) || null;
  const supply = reference.equipment.find((e) => e.equipmentTypeId === supplyId);
  const [region, setRegion] = useState('all');
  const [groupId, setGroupId] = useState('all');
  const [typeId, setTypeId] = useState('all');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [draft, setDraft] = useState<LocationDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return reference.locations
      .filter((l) => (showInactive || l.isActive)
        && (region === 'all' || l.regionCode === region)
        && (groupId === 'all' || String(l.groupId) === groupId)
        && (typeId === 'all' || String(l.locationTypeId) === typeId)
        && (!supply || !!typeOf(l.locationTypeId)?.equipmentTypeIds.includes(supply.equipmentTypeId))
        && (!q || l.code.toLowerCase().includes(q) || (l.city ?? '').toLowerCase().includes(q)));
  }, [reference.locations, showInactive, region, groupId, typeId, search, supply, typeOf]);

  const inactiveCount = reference.locations.filter((l) => !l.isActive).length;
  const locationSort = useTableSort(rows, {
    code: (l) => l.code,
    region: (l) => regionName(l.regionCode),
    group: (l) => groupName(l.groupId),
    type: (l) => typeOf(l.locationTypeId)?.name,
    city: (l) => l.city,
    status: (l) => (l.isActive ? 'Active' : 'Inactive'),
  });

  const save = useMutation({
    mutationFn: (d: LocationDraft) => {
      const body = {
        code: d.code, regionCode: d.regionCode, groupId: d.groupId, locationTypeId: d.locationTypeId,
        city: d.city.trim() || null,
        isActive: d.isActive,
        rowVersionNo: d.rowVersionNo,
      };
      return d.locationId > 0 ? api.put(`/api/setup/locations/${d.locationId}`, body) : api.post('/api/setup/locations', body);
    },
    onSuccess: (_r, d) => {
      setDraft(null); setError(null); setConflict(null);
      setNotice(`${d.code.trim()} saved.`);
      void queryClient.invalidateQueries({ queryKey: ['reference'] });
    },
    onError: (e: Error, d) => (e instanceof ConflictError && d.locationId > 0 ? setConflict(e.message) : setError(e.message)),
  });

  const blank = (): LocationDraft => ({
    locationId: 0, code: '', regionCode: reference.regions[0]?.regionCode ?? '',
    groupId: reference.groups[0]?.groupId ?? 0, locationTypeId: reference.types[0]?.locationTypeId ?? 0,
    city: '', isActive: true, rowVersionNo: 0,
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Locations</div>
          <div className="page-subtitle">
            {reference.locations.filter((l) => l.isActive).length} active
            {inactiveCount > 0 ? ` · ${inactiveCount} inactive` : ''} · every place that is counted
          </div>
        </div>
        {canEditSetup && !draft && (
          <button className="btn btn--primary" onClick={() => { setNotice(null); setDraft(blank()); }}>Add location</button>
        )}
      </div>

      {!canEditSetup && (
        <div className="meta" style={{ marginBottom: 12 }}>
          You can see the setup. Changing it needs Setup at all regions.
        </div>
      )}
      {notice && <div className="banner banner--info"><div>{notice}</div></div>}
      {conflict && (
        <ConflictBanner message={conflict} onReload={() => { setConflict(null); setDraft(null); void queryClient.invalidateQueries({ queryKey: ['reference'] }); }} />
      )}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {draft && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="section-label">{draft.locationId > 0 ? `EDIT ${draft.code.toUpperCase()}` : 'ADD A LOCATION'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 }}>
            <div>
              <label className="field-label" htmlFor="loc-code">UNIT ID</label>
              <input id="loc-code" className="control" style={{ width: '100%' }} placeholder="GKC613"
                value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
            </div>
            <div>
              <label className="field-label" htmlFor="loc-region">REGION</label>
              <select id="loc-region" className="control" style={{ width: '100%' }} value={draft.regionCode}
                onChange={(e) => setDraft({ ...draft, regionCode: e.target.value })}>
                {reference.regions.map((r) => <option key={r.regionCode} value={r.regionCode}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="loc-group">GROUP</label>
              <select id="loc-group" className="control" style={{ width: '100%' }} value={draft.groupId}
                onChange={(e) => setDraft({ ...draft, groupId: Number(e.target.value) })}>
                {reference.groups.map((g) => (
                  <option key={g.groupId} value={g.groupId}>{g.name}{g.isActive ? '' : ' (inactive)'}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="loc-type">TYPE</label>
              <select id="loc-type" className="control" style={{ width: '100%' }} value={draft.locationTypeId}
                onChange={(e) => setDraft({ ...draft, locationTypeId: Number(e.target.value) })}>
                {reference.types.map((t) => <option key={t.locationTypeId} value={t.locationTypeId}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label" htmlFor="loc-city">CITY</label>
              <input id="loc-city" className="control" style={{ width: '100%' }} placeholder="Kansas City, KS"
                value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14 }}>
            <input type="checkbox" checked={draft.isActive} onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })} />
            Active — an inactive location takes no new counts and leaves the rollup, but keeps its history
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn--primary" disabled={!draft.code.trim() || save.isPending}
              onClick={() => { setError(null); save.mutate(draft); }}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            <button className="btn" onClick={() => { setDraft(null); setError(null); }}>Cancel</button>
          </div>
        </div>
      )}

      <div className="filter-row">
        <input className="control" style={{ width: 200 }} placeholder="Search unit ID or city"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="control" aria-label="Region" value={region} onChange={(e) => setRegion(e.target.value)}>
          <option value="all">All regions</option>
          {reference.regions.map((r) => <option key={r.regionCode} value={r.regionCode}>{r.name}</option>)}
        </select>
        <select className="control" aria-label="Group" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
          <option value="all">All groups</option>
          {reference.groups.map((g) => <option key={g.groupId} value={g.groupId}>{g.name}</option>)}
        </select>
        <select className="control" aria-label="Type" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
          <option value="all">All types</option>
          {reference.types.map((t) => <option key={t.locationTypeId} value={t.locationTypeId}>{t.name}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        {supply && (
          <span className="chip">
            Counting {supply.name} · {rows.length}
            <button
              aria-label={`Show all locations, not only those counting ${supply.name}`}
              title="Show all locations"
              onClick={() => setParams({}, { replace: true })}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', padding: 0, fontSize: 14, lineHeight: 1 }}
            >
              ×
            </button>
          </span>
        )}
      </div>

      <div className="table-wrap" style={{ marginBottom: 24 }}>
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="UNIT ID" column="code" sort={locationSort.sort} onSort={locationSort.onSort} />
              <SortableHeader label="REGION" column="region" sort={locationSort.sort} onSort={locationSort.onSort} />
              <SortableHeader label="GROUP" column="group" sort={locationSort.sort} onSort={locationSort.onSort} />
              <SortableHeader label="TYPE" column="type" sort={locationSort.sort} onSort={locationSort.onSort} />
              <SortableHeader label="CITY" column="city" sort={locationSort.sort} onSort={locationSort.onSort} />
              <SortableHeader label="STATUS" column="status" sort={locationSort.sort} onSort={locationSort.onSort} />
              {canEditSetup && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow colSpan={canEditSetup ? 7 : 6}>No locations match.</EmptyRow>}
            {locationSort.rows.map((l) => (
              <tr key={l.locationId} style={l.isActive ? undefined : { color: 'var(--muted)' }}>
                <td className="strong">{l.code}</td>
                <td>{regionName(l.regionCode)}</td>
                <td>{groupName(l.groupId)}</td>
                <td>{typeOf(l.locationTypeId)?.name ?? '—'}</td>
                <td>{l.city ?? '—'}</td>
                <td>{l.isActive ? 'Active' : 'Inactive'}</td>
                {canEditSetup && (
                  <td className="num">
                    <button className="btn btn--sm" onClick={() => { setNotice(null); setError(null); setDraft(toDraft(l)); }}>
                      Edit
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Groups groups={reference.groups} canEdit={canEditSetup} onSaved={setNotice} />
    </div>
  );
}

/** The rollup's columns. Renamed or deactivated here, never deleted. */
function Groups({ groups, canEdit, onSaved }: { groups: LocationGroup[]; canEdit: boolean; onSaved: (message: string) => void }) {
  const { reference } = useApp();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<LocationGroup | null>(null);
  const activeIn = (groupId: number) => reference.locations.filter((l) => l.groupId === groupId && l.isActive).length;
  const { rows, sort, onSort } = useTableSort(groups, {
    name: (g) => g.name,
    heading: (g) => g.shortName,
    locations: (g) => activeIn(g.groupId),
    status: (g) => (g.isActive ? 'Active' : 'Inactive'),
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (g: LocationGroup) => {
      const body = { name: g.name, shortName: g.shortName, isActive: g.isActive, rowVersionNo: g.rowVersionNo };
      return g.groupId > 0 ? api.put(`/api/setup/groups/${g.groupId}`, body) : api.post('/api/setup/groups', body);
    },
    onSuccess: (_r, g) => {
      setEditing(null); setError(null);
      onSaved(`Group ${g.name.trim()} saved.`);
      void queryClient.invalidateQueries({ queryKey: ['reference'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <>
      <div className="page-header" style={{ marginBottom: 10 }}>
        <div className="section-label" style={{ marginBottom: 0 }}>GROUPS — THE ROLLUP'S COLUMNS</div>
        {canEdit && !editing && (
          <button className="btn btn--sm" onClick={() => setEditing({ groupId: 0, name: '', shortName: '', isActive: true, rowVersionNo: 0 })}>
            Add group
          </button>
        )}
      </div>
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {editing && (
        <div className="card" style={{ marginBottom: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label className="field-label" htmlFor="grp-name">NAME</label>
            <input id="grp-name" className="control" value={editing.name} placeholder="Newton Treatment Facility"
              onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
          </div>
          <div>
            <label className="field-label" htmlFor="grp-short">COLUMN HEADING</label>
            <input id="grp-short" className="control" style={{ width: 150 }} value={editing.shortName} placeholder="Newton"
              onChange={(e) => setEditing({ ...editing, shortName: e.target.value })} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, paddingBottom: 8 }}>
            <input type="checkbox" checked={editing.isActive} onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} />
            Active
          </label>
          <button className="btn btn--primary" disabled={!editing.name.trim() || save.isPending} onClick={() => save.mutate(editing)}>
            {save.isPending ? 'Saving…' : 'Save group'}
          </button>
          <button className="btn" onClick={() => { setEditing(null); setError(null); }}>Cancel</button>
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="NAME" column="name" sort={sort} onSort={onSort} />
              <SortableHeader label="COLUMN HEADING" column="heading" sort={sort} onSort={onSort} />
              <SortableHeader label="LOCATIONS" column="locations" sort={sort} onSort={onSort} className="num" />
              <SortableHeader label="STATUS" column="status" sort={sort} onSort={onSort} />
              {canEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((g) => (
              <tr key={g.groupId} style={g.isActive ? undefined : { color: 'var(--muted)' }}>
                <td className="strong">{g.name}</td>
                <td>{g.shortName}</td>
                <td className="num">{activeIn(g.groupId)}</td>
                <td>{g.isActive ? 'Active' : 'Inactive'}</td>
                {canEdit && (
                  <td className="num"><button className="btn btn--sm" onClick={() => setEditing(g)}>Edit</button></td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
